import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { moodColor, moodEmoji, moodLabel } from "@/lib/mood";
import { Chip, RangeField } from "@/components/kit";
import { ChipGroup, slug, toggleIn } from "@/components/checkin/ChipGroup";
import { STRUGGLES, MOOD_TAGS, DAILY_CHALLENGES, SLEEP_QUALITY_LABELS } from "@/data/checkin";
import { CustomEmotions } from "@/components/CustomEmotions";
import { useCustomEmotions } from "@/hooks/useCustomEmotions";

export const moodPrompt = (m) => {
  if (m >= 8) return "You're doing well! What's contributing to this positive feeling?";
  if (m >= 6) return "Things seem okay. Is there anything you want to acknowledge or celebrate?";
  if (m >= 4) return "How are you really doing right now? What would help you feel a bit better?";
  if (m >= 2) return "This sounds like a hard day. What does your inner voice need to hear right now?";
  return "You're struggling and that's okay. What's weighing on you the most right now?";
};

export const StepMood = ({ f, set }) => (
  <div className="text-center" data-testid="checkin-step-mood">
    <div className="text-7xl transition-transform" style={{ transform: `scale(${0.9 + f.mood / 40})` }}>{moodEmoji(f.mood)}</div>
    <div className="font-display text-4xl font-bold mt-3" style={{ color: moodColor(f.mood) }}>{f.mood}/10</div>
    <div className="text-mm-muted font-semibold">{moodLabel(f.mood)}</div>
    <div className="mt-8 text-left"><RangeField label="How's your mood right now?" value={f.mood} onChange={set("mood")} min={1} max={10} testId="checkin-mood-slider" left="Struggling" right="Excellent" /></div>
    <p className="mm-soft p-4 text-sm text-left">{moodPrompt(f.mood)}</p>
  </div>
);

const WoundDown = () => {
  const [did, setDid] = useState(false);
  useEffect(() => { api.get("/bedtime/status", { params: { tz: Intl.DateTimeFormat().resolvedOptions().timeZone } }).then((r) => setDid(r.data.last_night)).catch(() => {}); }, []);
  return did ? <div className="mm-soft p-3 mb-4 text-sm font-medium" data-testid="checkin-winddown-note">🌙 You did your wind-down last night. How did you sleep?</div> : null;
};

export const StepBody = ({ f, set }) => (
  <div data-testid="checkin-step-body">
    <WoundDown />
    <RangeField label="⚡ Energy" value={f.energy} onChange={set("energy")} min={1} max={10} testId="checkin-energy-slider" left="Drained" right="Energized" />
    <RangeField label="😴 Sleep hours" value={f.sleepHours} onChange={set("sleepHours")} min={0} max={14} step={0.5} display={`${f.sleepHours}h`} testId="checkin-sleep-hours-slider" />
    <RangeField label="🌙 Sleep quality" value={f.sleepQuality} onChange={set("sleepQuality")} min={1} max={5} display={SLEEP_QUALITY_LABELS[f.sleepQuality]} testId="checkin-sleep-quality-slider" />
    <RangeField label="😰 Anxiety / Stress" value={f.anxiety} onChange={set("anxiety")} testId="checkin-anxiety-slider" left="Calm" right="Intense" />
    <RangeField label="🩺 Symptom intensity" value={f.symptom} onChange={set("symptom")} testId="checkin-symptom-slider" left="None" right="Severe" />
    <RangeField label="🧗 Challenge level" value={f.challengeLevel} onChange={set("challengeLevel")} testId="checkin-challenge-slider" left="Easy day" right="Very hard" />
  </div>
);

export const StepActivities = ({ f, set, items, saveItem }) => (
  <ChipGroup title="What did you do today?" items={items.activities} selected={f.activities} onChange={set("activities")} prefix="checkin-activity" onAdd={saveItem("habit")} />
);

export const StepTriggers = ({ f, set, items, saveItem }) => (
  <>
    <ChipGroup title="Any triggers today?" items={items.triggers} selected={f.triggers} onChange={set("triggers")} prefix="checkin-trigger" onAdd={saveItem("trigger")} />
    <ChipGroup title="What are you struggling with?" items={STRUGGLES} selected={f.struggles} onChange={set("struggles")} prefix="checkin-struggle" />
  </>
);

export const StepJournal = ({ f, set }) => {
  const custom = useCustomEmotions();
  const tag = (e) => `${e.emoji} ${e.label}`;
  return (
  <div data-testid="checkin-step-journal">
    <h3 className="font-semibold mb-3">How would you describe it?</h3>
    <div className="flex flex-wrap gap-2 mb-6">
      {MOOD_TAGS.map((t) => <Chip key={t} on={f.tags.includes(t)} onClick={() => set("tags")(toggleIn(f.tags, t))} testId={`checkin-tag-${slug(t)}`}>{t}</Chip>)}
      <CustomEmotions emotions={custom.items} isOn={(e) => f.tags.includes(tag(e))} onToggle={(e) => set("tags")(toggleIn(f.tags, tag(e)))}
        onAdd={custom.add} onRemove={(id) => { const e = custom.items.find((x) => x.id === id); if (e) set("tags")(f.tags.filter((t) => t !== tag(e))); custom.remove(id); }} />
    </div>
    <h3 className="font-semibold mb-2">Journal</h3>
    <p className="text-sm text-mm-muted mb-3">{moodPrompt(f.mood)}</p>
    <textarea className="mm-input min-h-[160px]" placeholder="Write freely — this is just for you." value={f.journal} onChange={(e) => set("journal")(e.target.value)} data-testid="checkin-journal-input" />
  </div>
  );
};

export const StepChallenges = ({ f, set }) => (
  <div data-testid="checkin-step-challenges">
    <h3 className="font-semibold mb-3">Tiny daily challenges</h3>
    <div className="grid sm:grid-cols-2 gap-3 mb-6">
      {DAILY_CHALLENGES.map((c) => (
        <button key={c.id} onClick={() => set("done")(toggleIn(f.done, c.id))} data-testid={`checkin-challenge-${c.id}`}
          className={`text-left p-4 rounded-2xl border-2 transition-colors ${f.done.includes(c.id) ? "border-mm-primary bg-mm-soft" : "border-mm-line"}`}>
          <div className="flex justify-between"><span className="text-2xl">{c.emoji}</span><span className="text-xs font-bold text-mm-primary">+{c.points} XP</span></div>
          <div className="font-semibold mt-2">{c.title}</div>
          <div className="text-xs text-mm-muted">{c.description}</div>
        </button>
      ))}
    </div>
    <h3 className="font-semibold mb-2">🙏 One thing you're grateful for</h3>
    <input className="mm-input" value={f.gratitude} onChange={(e) => set("gratitude")(e.target.value)} placeholder="Something small counts" data-testid="checkin-gratitude-input" />
  </div>
);

export const STEP_COMPONENTS = [StepMood, StepBody, StepActivities, StepTriggers, StepJournal, StepChallenges];
export const STEP_NAMES = ["Mood", "Body & Mind", "Activities", "Triggers", "Journal", "Challenges"];
